import { Route, Switch, Redirect } from "wouter";
import { ProtectedRoute } from "@/lib/protected-route";
import { useAuth } from "@/hooks/use-auth";
import NotFound from "@/pages/not-found";
import AuthPage from "@/pages/auth-page";
import PropertyDetail from "@/pages/property-detail";
import PropertiesPage from "@/pages/properties";
import PropertyLinkPage from "@/pages/properties/link";
import ContactAgentPage from "@/pages/contact-agent";
import InquiryPage from "@/pages/inquiry";
import HowItWorksPage from "@/pages/how-it-works";
import TermsOfServicePage from "@/pages/terms";
import PrivacyPage from "@/pages/privacy";
import CookiesPage from "@/pages/cookies";
import AgentTermsPage from "@/pages/agent-terms";
import ManagerTermsPage from "@/pages/manager-terms";
import FirebaseTestPage from "@/pages/firebase-test";
import FirebaseConfigTestPage from "@/pages/firebase-config-test";
import AuthTestPage from "@/pages/auth-test";
import DashboardPage from "@/pages/dashboard";
import BookingPayment from "@/pages/booking-payment";
import StripeConnectPage from "@/pages/stripe-connect";
import SettingsPage from "@/pages/settings";
import ManagerPropertiesPage from "@/pages/manager/properties";
import ManagerDashboardPage from "@/pages/manager/dashboard";
import ManagerCalendarPage from "@/pages/manager/calendar-enhanced-v5";
import ManagerBookingsPage from "@/pages/manager/bookings";
import ManagerEarningsPage from "@/pages/manager/earnings";
import ManagerAirbnbUrlImportPage from "@/pages/manager/import-property";
import ManagerLinksPage from "@/pages/manager/links";
import EditPropertyPage from "@/pages/manager/edit-property";
import HomePage from "@/pages/home";
import { UserRole } from "@/lib/types";
import { Layout } from "@/components/layout/Layout";
import AgentDashboardPage from "@/pages/agent/dashboard";
import AgentLinksPage from "@/pages/agent/links";
import AgentCommissionsPage from "@/pages/agent/commissions";
import AgentBookingsPage from "@/pages/agent/bookings";
import AdminDashboardPage from "@/pages/admin/index";
import AdminPropertiesPage from "@/pages/admin/properties";
import AdminUsersPage from "@/pages/admin/users";
import AdminBookingsPage from "@/pages/admin/bookings";
import AdminSettingsPage from "@/pages/admin/settings";
import CommunityPage from "@/pages/manager/community";
import CommunityQuestionPage from "@/pages/manager/community-question";
import TestScraperPage from "@/pages/test-scraper";
import DebugScraperPage from "@/pages/debug-scraper";
import SimpleTestPage from "@/pages/simple-test";
import BookingConfirmationPage from "@/pages/bookings/confirmation";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { 
  Building, 
  Search, 
  Calendar, 
  CreditCard, 
  Users, 
  Landmark,
  Wallet,
  Shield,
  Medal,
  Star,
  ChevronRight
} from "lucide-react";
import ManagerCalendarByIdPage from "@/pages/manager/calendar/[id]";
import GuestBookingsPage from "./pages/bookings/bookings";
import LoginRedirect from './pages/login';
import RegisterRedirect from './pages/register';
import RegisterManagerRedirect from './pages/register/manager';
import RegisterAgentRedirect from './pages/register/agent';
import DelloWidget from "@/components/ui/DelloWidget";

// The HomePage component is now imported from @/pages/home

function PendingApprovalPage() {
  const { user } = useAuth();
  
  if (!user) {
    return <Redirect to="/auth" />;
  }
  
  if (user.status === "approved") {
    return <Redirect to="/dashboard" />;
  }

  return (
    <Layout>
      <div className="container py-16 md:py-24">
        <div className="max-w-md mx-auto bg-background p-8 rounded-lg border shadow-sm">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-16 h-16 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mb-4">
              <svg 
                className="w-8 h-8" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" 
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold">Account Pending Approval</h1>
          </div>
          
          <div className="space-y-4 mb-6">
            <p className="text-muted-foreground">
              Your account is currently pending approval from our administrators. This process typically takes 1-2 business days.
            </p>
            
            {user.status === "rejected" && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg 
                      className="h-5 w-5 text-red-600" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Account Rejected</h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>
                        {user.rejectionReason || "Your account was rejected. Please contact our support team for more information."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg 
                    className="h-5 w-5 text-blue-600" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <div className="text-sm text-blue-700">
                    <p>
                      You will receive an email notification once your account has been reviewed.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="space-y-3">
            <Button
              className="w-full"
              variant="outline"
              onClick={() => window.location.reload()}
            >
              Check Status Again
            </Button>
            
            <Link href="/auth">
              <Button
                className="w-full"
                variant="link"
              >
                Back to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Router() {
  return (
    <Switch>
      {/* Public routes */}
      <Route path="/" component={HomePage} />
      <Route path="/auth" component={AuthPage} />
      <Route path="/login" component={LoginRedirect} />
      <Route path="/register" component={RegisterRedirect} />
      <Route path="/register/manager" component={RegisterManagerRedirect} />
      <Route path="/register/agent" component={RegisterAgentRedirect} />
      <Route path="/pending-approval" component={PendingApprovalPage} />
      <Route path="/inquiry" component={InquiryPage} />
      {/* Link-based property detail */}
      <Route path="/properties/:propertyId/link/:linkCode">
        {(params) => <PropertyDetail id={parseInt(params.propertyId)} linkCode={params.linkCode} />}
      </Route>
      {/* Link-based property list */}
      <Route path="/properties/link/:linkCode">
        {(params) => <PropertiesPage linkCode={params.linkCode} />}
      </Route>
      {/* Public property detail */}
      <Route path="/properties/:propertyId">
        {(params) => <PropertyDetail id={parseInt(params.propertyId)} />}
      </Route>
      {/* Public property list */}
      <Route path="/properties" component={PropertiesPage} />
      <Route path="/booking-payment/:id">
        {(params) => <BookingPayment bookingId={parseInt(params.id)} key={params.id} />}
      </Route>
      <Route path="/stripe-connect" component={StripeConnectPage} />
      <Route path="/settings" component={SettingsPage} />
      <Route path="/contact-agent" component={ContactAgentPage} />
      <Route path="/how-it-works" component={HowItWorksPage} />
      
      <Route path="/test-scraper" component={TestScraperPage} />
      <Route path="/debug-scraper" component={DebugScraperPage} />
      <Route path="/simple-test" component={SimpleTestPage} />
      <Route path="/firebase-test" component={FirebaseTestPage} />
      <Route path="/firebase-config-test" component={FirebaseConfigTestPage} />
      <Route path="/auth-test" component={AuthTestPage} />
      
      {/* Legal Pages */}
      <Route path="/terms" component={TermsOfServicePage} />
      <Route path="/privacy" component={PrivacyPage} />
      <Route path="/cookies" component={CookiesPage} />
      <Route path="/agent-terms" component={AgentTermsPage} />
      <Route path="/manager-terms" component={ManagerTermsPage} />
      

      {/* Guest Routes */}    
      <ProtectedRoute path="/dashboard" component={DashboardPage}     roles={[UserRole.MANAGER, UserRole.GUEST]}  />

      <ProtectedRoute path="/bookings" component={GuestBookingsPage} roles={[UserRole.MANAGER, UserRole.GUEST]} />
      <ProtectedRoute path="/bookings/:id/confirmation" component={BookingConfirmationPage} roles={[UserRole.MANAGER, UserRole.GUEST]} >
         {(params) => <BookingConfirmationPage id={params.id} />}
       </ProtectedRoute>
      {/* Property Manager Routes */}
      <ProtectedRoute 
        path="/manager/dashboard" 
        component={ManagerDashboardPage} 
        roles={[UserRole.MANAGER, UserRole.ADMIN]}
      />
      <ProtectedRoute 
        path="/manager/properties" 
        component={ManagerPropertiesPage} 
        roles={[UserRole.MANAGER, UserRole.ADMIN]}
      />
      <ProtectedRoute 
        path="/manager/calendar" 
        component={ManagerCalendarPage} 
        roles={[UserRole.MANAGER, UserRole.ADMIN]}
      />
      <ProtectedRoute 
        path="/manager/calendar/:id" 
        component={ManagerCalendarByIdPage} 
        roles={[UserRole.MANAGER, UserRole.ADMIN]}
      />
      <ProtectedRoute 
        path="/manager/bookings" 
        component={ManagerBookingsPage} 
        roles={[UserRole.MANAGER, UserRole.ADMIN]}
      />
      <ProtectedRoute 
        path="/manager/earnings" 
        component={ManagerEarningsPage} 
        roles={[UserRole.MANAGER, UserRole.ADMIN]}
      />
      
      <ProtectedRoute 
        path="/manager/import-property" 
        component={ManagerAirbnbUrlImportPage} 
        roles={[UserRole.MANAGER, UserRole.ADMIN]}
      />
      <ProtectedRoute 
        path="/manager/links" 
        component={ManagerLinksPage} 
        roles={[UserRole.MANAGER, UserRole.ADMIN]}
      />
      <ProtectedRoute 
        path="/manager/edit-property/:id" 
        component={EditPropertyPage} 
        roles={[UserRole.MANAGER, UserRole.ADMIN]}
      />

      <ProtectedRoute 
        path="/manager/community" 
        component={CommunityPage} 
        roles={[UserRole.MANAGER, UserRole.ADMIN]}
      />
      <ProtectedRoute 
        path="/manager/community/:id" 
        component={CommunityQuestionPage} 
        roles={[UserRole.MANAGER, UserRole.ADMIN]}
      />
      
      {/* Agent Routes */}
      <ProtectedRoute 
        path="/agent/dashboard" 
        component={AgentDashboardPage} 
        roles={[UserRole.AGENT, UserRole.ADMIN]}
      />
      <ProtectedRoute 
        path="/agent/links" 
        component={AgentLinksPage} 
        roles={[UserRole.AGENT, UserRole.ADMIN]}
      />
      <ProtectedRoute 
        path="/agent/links/create" 
        component={AgentLinksPage} 
        roles={[UserRole.AGENT, UserRole.ADMIN]}
      />
      <ProtectedRoute 
        path="/agent/commissions" 
        component={AgentCommissionsPage} 
        roles={[UserRole.AGENT, UserRole.ADMIN]}
      />
       {/* <ProtectedRoute 
        path="/agent/clients" 
        component={AgentClientsPage} 
        roles={[UserRole.AGENT, UserRole.ADMIN]}
      /> */}
      <ProtectedRoute 
        path="/agent/bookings" 
        component={AgentBookingsPage} 
        roles={[UserRole.AGENT, UserRole.ADMIN]}
      />
      
      
      {/* Admin Routes (with new component-based protection) */}
      <Route path="/admin">
        {() => {
          const { user } = useAuth();
          if (user?.role === UserRole.ADMIN ) {
            return <AdminDashboardPage />;
          }
          return <NotFound />;
        }}
      </Route>
      
      <Route path="/admin/users">
        {() => {
          const { user } = useAuth();
          if (user?.role === UserRole.ADMIN) {
            return <AdminUsersPage />;
          }
          return <NotFound />;
        }}
      </Route>
      
      <Route path="/admin/properties">
        {() => {
          const { user } = useAuth();
          if (user?.role === UserRole.ADMIN ) {
            return <AdminPropertiesPage />;
          }
          return <NotFound />;
        }}
      </Route>
      
      <Route path="/admin/bookings">
        {() => {
          const { user } = useAuth();
          if (user?.role === UserRole.ADMIN ) {
            return <AdminBookingsPage />;
          }
          return <NotFound />;
        }}
      </Route>
      
      <Route path="/admin/properties/:id">
        {(params) => {
          const { user } = useAuth();
          if (user?.role === UserRole.ADMIN ) {
            return <PropertyDetail id={parseInt(params.id)} isAdmin={true} />;
          }
          return <NotFound />;
        }}
      </Route>

      <Route path="/admin/settings">
        {() => {
          const { user } = useAuth();
          if (user?.role === UserRole.ADMIN ) {
            return <AdminSettingsPage />;
          }
          return <NotFound />;
        }}
      </Route>
      

      
      {/* Fallback */}
      <Route component={NotFound} />
    </Switch>
  );
}

export default function App() {
  const { user } = useAuth();
  return (
    <>
      <Router />
      {(!user || user?.role === UserRole.GUEST ) &&  <DelloWidget />}
    </>
  );
}